import { MapElement, MapSettings } from '../../../types'
import { Point } from './types'
import { Tool } from '../ToolsPanel'
import {
  drawWallElement,
  drawDoorElement,
  drawWindowElement,
  drawRoomElement,
  drawPolygonElement,
} from '../canvas/elementRenderers'
import { drawWallWithJoins } from '../canvas/cornerJoining'

const drawElement = (
  ctx: CanvasRenderingContext2D,
  element: MapElement,
  isSelected: boolean,
  isOnCurrentFloor: boolean = true,
  mapSettings: MapSettings,
  allElements: MapElement[] = [],
) => {
  ctx.save()

  // Store current alpha (might be set for fade)
  const currentAlpha = ctx.globalAlpha

  // Apply opacity for elements not on current floor
  if (!isOnCurrentFloor) {
    ctx.globalAlpha = currentAlpha * 0.3
  } else if (element.properties.opacity !== undefined) {
    ctx.globalAlpha = currentAlpha * element.properties.opacity
  }

  // Set styles based on element properties
  ctx.strokeStyle = element.properties.color || '#333'
  ctx.fillStyle = element.properties.fillColor || 'transparent'
  ctx.lineWidth = element.properties.strokeWidth || 2

  switch (element.type) {
    case 'wall':
      // Use smart corner joining for walls
      ctx.save()
      ctx.globalAlpha = currentAlpha
      drawWallWithJoins(ctx, element, allElements, element.properties.thickness || 6)
      ctx.restore()

      // Draw selection highlight if needed
      if (isSelected) {
        ctx.strokeStyle = '#007BFF'
        ctx.lineWidth = 2
        ctx.setLineDash([5, 5])
        ctx.beginPath()
        ctx.moveTo(element.position.x, element.position.y)
        if (element.properties.endPosition) {
          ctx.lineTo(element.properties.endPosition.x, element.properties.endPosition.y)
        }
        ctx.stroke()
        ctx.setLineDash([])
      }
      break

    case 'door':
      drawDoorElement(ctx, element, isSelected, isOnCurrentFloor)
      break

    case 'window':
      drawWindowElement(ctx, element, isSelected, isOnCurrentFloor)
      break

    case 'room':
      drawRoomElement(ctx, element, isSelected, isOnCurrentFloor, mapSettings)
      break

    case 'polygon':
      drawPolygonElement(
        ctx,
        element,
        isSelected,
        isOnCurrentFloor,
        mapSettings,
      )
      break

    case 'stairs':
      drawStairsElement(ctx, element)
      break

    case 'poi':
      drawPoiElement(ctx, element)
      break

    case 'measurement':
      drawMeasurementElement(ctx, element, mapSettings)
      break

    case 'line':
      drawLineElement(ctx, element)
      break

    case 'rectangle':
      drawRectangleElement(ctx, element)
      break

    case 'circle':
      drawCircleElement(ctx, element)
      break

    case 'text':
      drawTextElement(ctx, element)
      break

    case 'fixture-rect':
      drawFixtureRectElement(ctx, element)
      break

    case 'fixture-line':
      drawFixtureLineElement(ctx, element)
      break

    case 'fixture-point':
      drawFixturePointElement(ctx, element)
      break

    case 'fixture-circle':
      drawFixtureCircleElement(ctx, element)
      break

    case 'fixture-polygon':
      drawFixturePolygonElement(ctx, element, mapSettings)
      break
  }

  ctx.restore()
}

const drawStairsElement = (
  ctx: CanvasRenderingContext2D,
  element: MapElement,
) => {
  if (element.properties.width && element.properties.height) {
    const steps = element.properties.steps || 12
    const stepWidth = element.properties.width / steps
    const material = element.properties.material || 'wood'
    const direction = element.properties.direction || 'up'
    const labelRotation = element.properties.labelRotation || 0

    // Different fill colors based on material
    let fillColor = element.properties.fillColor
    if (!fillColor) {
      switch (material) {
        case 'concrete':
          fillColor = '#C0C0C0'
          break
        case 'metal':
          fillColor = '#B0B0B0'
          break
        case 'carpet':
          fillColor = '#8B4513'
          break
        default:
          fillColor = '#ddd' // wood
      }
    }

    ctx.fillStyle = fillColor
    ctx.fillRect(
      element.position.x,
      element.position.y,
      element.properties.width,
      element.properties.height,
    )

    // Draw step lines - check orientation
    const stairOrientation = element.properties.stairOrientation || 'horizontal'
    ctx.strokeStyle = '#999'
    ctx.lineWidth = 1

    if (stairOrientation === 'horizontal') {
      // Horizontal lines (original behavior)
      const stepSize = element.properties.width / steps
      for (let i = 1; i < steps; i++) {
        const x = element.position.x + stepSize * i
        ctx.beginPath()
        ctx.moveTo(x, element.position.y)
        ctx.lineTo(x, element.position.y + element.properties.height)
        ctx.stroke()
      }
    } else {
      // Vertical lines
      const stepSize = element.properties.height / steps
      for (let i = 1; i < steps; i++) {
        const y = element.position.y + stepSize * i
        ctx.beginPath()
        ctx.moveTo(element.position.x, y)
        ctx.lineTo(element.position.x + element.properties.width, y)
        ctx.stroke()
      }
    }

    // Draw border
    ctx.strokeStyle = element.properties.color || '#333'
    ctx.lineWidth = element.properties.strokeWidth || 2
    ctx.strokeRect(
      element.position.x,
      element.position.y,
      element.properties.width,
      element.properties.height,
    )

    // Draw direction arrow, text, and label all together with rotation
    ctx.save()
    ctx.fillStyle = element.properties.color || '#333'
    const centerX = element.position.x + element.properties.width / 2
    const centerY = element.position.y + element.properties.height / 2

    // Apply rotation for all text and arrow elements
    ctx.translate(centerX, centerY)
    ctx.rotate((labelRotation * Math.PI) / 180)

    // Draw direction text
    ctx.font = 'bold 16px Arial'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'

    const arrowSize = 20

    if (direction === 'up') {
      // Draw up arrow
      ctx.beginPath()
      ctx.moveTo(0, -arrowSize)
      ctx.lineTo(-arrowSize / 2, 0)
      ctx.lineTo(-arrowSize / 4, 0)
      ctx.lineTo(-arrowSize / 4, arrowSize)
      ctx.lineTo(arrowSize / 4, arrowSize)
      ctx.lineTo(arrowSize / 4, 0)
      ctx.lineTo(arrowSize / 2, 0)
      ctx.closePath()
      ctx.fill()
      ctx.fillText('UP', 0, arrowSize + 20)

      // Draw label above the arrow if present
      if (element.properties.label) {
        ctx.font = 'bold 14px Arial'
        const labelOffsetX = element.labelPosition?.x || 0
        const labelOffsetY = element.labelPosition?.y || 0
        ctx.fillText(
          element.properties.label,
          labelOffsetX,
          -arrowSize - 30 + labelOffsetY,
        )
      }
    } else {
      // Draw down arrow
      ctx.beginPath()
      ctx.moveTo(0, arrowSize)
      ctx.lineTo(-arrowSize / 2, 0)
      ctx.lineTo(-arrowSize / 4, 0)
      ctx.lineTo(-arrowSize / 4, -arrowSize)
      ctx.lineTo(arrowSize / 4, -arrowSize)
      ctx.lineTo(arrowSize / 4, 0)
      ctx.lineTo(arrowSize / 2, 0)
      ctx.closePath()
      ctx.fill()
      ctx.fillText('DOWN', 0, -arrowSize - 20)

      // Draw label below the arrow if present
      if (element.properties.label) {
        ctx.font = 'bold 14px Arial'
        const labelOffsetX = element.labelPosition?.x || 0
        const labelOffsetY = element.labelPosition?.y || 0
        ctx.fillText(
          element.properties.label,
          labelOffsetX,
          arrowSize + 35 + labelOffsetY,
        )
      }
    }

    ctx.restore()
  }
}

const drawPoiElement = (ctx: CanvasRenderingContext2D, element: MapElement) => {
  const size = element.properties.size || 8
  const category = element.properties.category || 'general'

  // Draw circle background
  ctx.fillStyle = element.properties.color || '#e74c3c'
  ctx.beginPath()
  ctx.arc(element.position.x, element.position.y, size, 0, 2 * Math.PI)
  ctx.fill()

  // Draw category-specific icon
  ctx.save()
  ctx.fillStyle = 'white'
  ctx.strokeStyle = 'white'
  ctx.lineWidth = 2
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'

  const iconSize = size * 0.6
  const x = element.position.x
  const y = element.position.y

  switch (category) {
    case 'safety':
      // Fire extinguisher icon
      ctx.beginPath()
      ctx.moveTo(x - iconSize / 2, y - iconSize / 2)
      ctx.lineTo(x + iconSize / 2, y - iconSize / 2)
      ctx.lineTo(x + iconSize / 2, y + iconSize / 2)
      ctx.lineTo(x - iconSize / 2, y + iconSize / 2)
      ctx.closePath()
      ctx.stroke()
      // Add handle
      ctx.beginPath()
      ctx.arc(x, y - iconSize / 2, iconSize / 4, Math.PI, 0, true)
      ctx.stroke()
      break

    case 'electrical':
      // Lightning bolt icon
      ctx.beginPath()
      ctx.moveTo(x - iconSize / 3, y - iconSize / 2)
      ctx.lineTo(x + iconSize / 4, y - iconSize / 4)
      ctx.lineTo(x, y)
      ctx.lineTo(x + iconSize / 3, y + iconSize / 2)
      ctx.lineTo(x - iconSize / 4, y + iconSize / 4)
      ctx.lineTo(x, y)
      ctx.stroke()
      break

    case 'plumbing':
      // Water drop icon
      ctx.beginPath()
      ctx.moveTo(x, y - iconSize / 2)
      ctx.quadraticCurveTo(x - iconSize / 2, y, x, y + iconSize / 2)
      ctx.quadraticCurveTo(x + iconSize / 2, y, x, y - iconSize / 2)
      ctx.fill()
      break

    case 'hvac':
      // Fan/vent icon
      ctx.beginPath()
      for (let i = 0; i < 4; i++) {
        const angle = (i * Math.PI) / 2
        ctx.moveTo(x, y)
        ctx.lineTo(
          x + Math.cos(angle) * iconSize,
          y + Math.sin(angle) * iconSize,
        )
      }
      ctx.stroke()
      ctx.beginPath()
      ctx.arc(x, y, iconSize / 4, 0, 2 * Math.PI)
      ctx.fill()
      break

    case 'security':
      // Lock icon
      ctx.beginPath()
      // Lock body
      ctx.rect(x - iconSize / 2, y - iconSize / 4, iconSize, iconSize / 2)
      ctx.fill()
      // Lock shackle
      ctx.beginPath()
      ctx.arc(x, y - iconSize / 4, iconSize / 3, Math.PI, 0, true)
      ctx.stroke()
      break

    case 'accessibility':
      // Wheelchair icon (simplified)
      ctx.beginPath()
      ctx.arc(x, y - iconSize / 3, iconSize / 4, 0, 2 * Math.PI)
      ctx.stroke()
      ctx.beginPath()
      ctx.arc(x, y + iconSize / 4, iconSize / 3, 0, 2 * Math.PI)
      ctx.stroke()
      break

    case 'furniture':
      // Chair icon (simplified)
      ctx.beginPath()
      ctx.rect(x - iconSize / 2, y - iconSize / 2, iconSize, iconSize * 0.6)
      ctx.stroke()
      ctx.beginPath()
      ctx.moveTo(x - iconSize / 2, y + iconSize * 0.1)
      ctx.lineTo(x - iconSize / 2, y + iconSize / 2)
      ctx.moveTo(x + iconSize / 2, y + iconSize * 0.1)
      ctx.lineTo(x + iconSize / 2, y + iconSize / 2)
      ctx.stroke()
      break

    case 'appliance':
      // Plug icon
      ctx.beginPath()
      ctx.moveTo(x - iconSize / 3, y - iconSize / 2)
      ctx.lineTo(x - iconSize / 3, y)
      ctx.moveTo(x + iconSize / 3, y - iconSize / 2)
      ctx.lineTo(x + iconSize / 3, y)
      ctx.moveTo(x - iconSize / 2, y)
      ctx.lineTo(x + iconSize / 2, y)
      ctx.lineTo(x + iconSize / 2, y + iconSize / 3)
      ctx.lineTo(x - iconSize / 2, y + iconSize / 3)
      ctx.closePath()
      ctx.stroke()
      break

    default: // general
      // Generic marker icon (star)
      ctx.beginPath()
      for (let i = 0; i < 5; i++) {
        const angle = (i * 2 * Math.PI) / 5 - Math.PI / 2
        const r = i % 2 === 0 ? iconSize : iconSize / 2
        const px = x + Math.cos(angle) * r
        const py = y + Math.sin(angle) * r
        if (i === 0) {
          ctx.moveTo(px, py)
        } else {
          ctx.lineTo(px, py)
        }
      }
      ctx.closePath()
      ctx.fill()
      break
  }

  ctx.restore()

  // Draw label if it exists
  if (element.properties.label) {
    ctx.fillStyle = element.properties.color || '#e74c3c'
    ctx.font = 'bold 14px Arial'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'top'
    ctx.fillText(
      element.properties.label,
      element.position.x,
      element.position.y + size + 4,
    )
  }
}

const drawMeasurementElement = (
  ctx: CanvasRenderingContext2D,
  element: MapElement,
  mapSettings: MapSettings,
) => {
  if (element.properties.endPosition) {
    ctx.save()
    ctx.strokeStyle = element.properties.strokeColor || '#ff6b6b'
    ctx.lineWidth = element.properties.strokeWidth || 2

    // Set dash pattern
    if (element.properties.dashPattern) {
      ctx.setLineDash(element.properties.dashPattern)
    } else {
      ctx.setLineDash([5, 5])
    }

    ctx.beginPath()
    ctx.moveTo(element.position.x, element.position.y)
    ctx.lineTo(
      element.properties.endPosition.x,
      element.properties.endPosition.y,
    )
    ctx.stroke()

    // Draw arrow heads
    const angle = Math.atan2(
      element.properties.endPosition.y - element.position.y,
      element.properties.endPosition.x - element.position.x,
    )
    const arrowSize = 10

    // Start arrow
    ctx.save()
    ctx.translate(element.position.x, element.position.y)
    ctx.rotate(angle)
    ctx.beginPath()
    ctx.moveTo(0, 0)
    ctx.lineTo(arrowSize, -arrowSize / 2)
    ctx.lineTo(arrowSize, arrowSize / 2)
    ctx.closePath()
    ctx.fill()
    ctx.restore()

    // End arrow
    ctx.save()
    ctx.translate(
      element.properties.endPosition.x,
      element.properties.endPosition.y,
    )
    ctx.rotate(angle + Math.PI)
    ctx.beginPath()
    ctx.moveTo(0, 0)
    ctx.lineTo(arrowSize, -arrowSize / 2)
    ctx.lineTo(arrowSize, arrowSize / 2)
    ctx.closePath()
    ctx.fill()
    ctx.restore()

    // Show distance text if enabled
    if (element.properties.showDistance) {
      const midX = (element.position.x + element.properties.endPosition.x) / 2
      const midY = (element.position.y + element.properties.endPosition.y) / 2

      const units = mapSettings.units === 'feet' ? 'ft' : 'm'
      const distanceText = `${element.properties.distance.toFixed(2)} ${units}`

      ctx.font = `${element.properties.fontSize || 14}px Arial`
      ctx.fillStyle = element.properties.strokeColor || '#ff6b6b'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'bottom'

      // Add background to text for better readability
      const textMetrics = ctx.measureText(distanceText)
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)'
      ctx.fillRect(
        midX - textMetrics.width / 2 - 4,
        midY - 20,
        textMetrics.width + 8,
        18,
      )

      ctx.fillStyle = element.properties.strokeColor || '#ff6b6b'
      ctx.fillText(distanceText, midX, midY - 4)
    }

    ctx.restore()
  }
}

const drawLineElement = (
  ctx: CanvasRenderingContext2D,
  element: MapElement,
) => {
  if (element.properties.endPosition) {
    ctx.beginPath()
    ctx.moveTo(element.position.x, element.position.y)
    ctx.lineTo(
      element.properties.endPosition.x,
      element.properties.endPosition.y,
    )
    ctx.stroke()
  }
}

const drawRectangleElement = (
  ctx: CanvasRenderingContext2D,
  element: MapElement,
) => {
  if (element.properties.width && element.properties.height) {
    ctx.beginPath()
    ctx.rect(
      element.position.x,
      element.position.y,
      element.properties.width,
      element.properties.height,
    )
    if (element.properties.fillColor) ctx.fill()
    ctx.stroke()
  }
}

const drawCircleElement = (
  ctx: CanvasRenderingContext2D,
  element: MapElement,
) => {
  if (element.properties.radius) {
    ctx.beginPath()
    ctx.arc(
      element.position.x + element.properties.radius,
      element.position.y + element.properties.radius,
      element.properties.radius,
      0,
      2 * Math.PI,
    )
    if (element.properties.fillColor) ctx.fill()
    ctx.stroke()
  }
}

const drawTextElement = (
  ctx: CanvasRenderingContext2D,
  element: MapElement,
) => {
  if (element.properties.text) {
    const fontSize = element.properties.fontSize || 16
    const fontFamily = element.properties.fontFamily || 'Arial'
    const bold = element.properties.bold ? 'bold ' : ''
    const italic = element.properties.italic ? 'italic ' : ''

    ctx.save()
    ctx.font = `${italic}${bold}${fontSize}px ${fontFamily}`
    ctx.fillStyle = element.properties.color || '#333'

    // Create larger hit area for labels
    const textMetrics = ctx.measureText(element.properties.text)
    const padding = 10 // Add padding for easier clicking

    // Store bounds for hit detection (this would need to be stored elsewhere in practice)
    const bounds = {
      x: element.position.x - padding,
      y: element.position.y - fontSize - padding,
      width: textMetrics.width + padding * 2,
      height: fontSize + padding * 2,
    }


    // Apply rotation if specified
    if (element.properties.rotation) {
      ctx.translate(element.position.x, element.position.y)
      ctx.rotate((element.properties.rotation * Math.PI) / 180)
      ctx.fillText(element.properties.text, 0, 0)
    } else {
      ctx.fillText(
        element.properties.text,
        element.position.x,
        element.position.y,
      )
    }
    ctx.restore()
  }
}

const drawFixtureRectElement = (
  ctx: CanvasRenderingContext2D,
  element: MapElement,
) => {
  if (element.properties.width && element.properties.height) {
    // Handle background image or fill color
    if (element.properties.backgroundImage) {
      ctx.save()
      ctx.beginPath()
      ctx.rect(
        element.position.x,
        element.position.y,
        element.properties.width,
        element.properties.height,
      )
      ctx.clip() // Clip to fixture shape

      // Get cached image from elementRenderers
      const img = new Image()
      img.src = element.properties.backgroundImage

      // Apply background image opacity if set
      const oldAlpha = ctx.globalAlpha
      ctx.globalAlpha = element.properties.backgroundImageOpacity ?? 1

      // Draw based on fit mode
      const fitMode = element.properties.backgroundImageFit || 'cover'
      if (img.complete) {
        switch (fitMode) {
          case 'contain':
            const scale = Math.min(
              element.properties.width / img.width,
              element.properties.height / img.height
            )
            const scaledWidth = img.width * scale
            const scaledHeight = img.height * scale
            const x = element.position.x + (element.properties.width - scaledWidth) / 2
            const y = element.position.y + (element.properties.height - scaledHeight) / 2
            ctx.drawImage(img, x, y, scaledWidth, scaledHeight)
            break
          case 'stretch':
            ctx.drawImage(
              img,
              element.position.x,
              element.position.y,
              element.properties.width,
              element.properties.height
            )
            break
          case 'repeat':
            const pattern = ctx.createPattern(img, 'repeat')
            if (pattern) {
              ctx.fillStyle = pattern
              ctx.fill()
            }
            break
          case 'cover':
          default:
            const coverScale = Math.max(
              element.properties.width / img.width,
              element.properties.height / img.height
            )
            const coverWidth = img.width * coverScale
            const coverHeight = img.height * coverScale
            const coverX = element.position.x + (element.properties.width - coverWidth) / 2
            const coverY = element.position.y + (element.properties.height - coverHeight) / 2
            ctx.drawImage(img, coverX, coverY, coverWidth, coverHeight)
            break
        }
      }

      ctx.globalAlpha = oldAlpha
      ctx.restore()
    } else {
      // Draw fixture rectangle with distinct styling
      ctx.fillStyle = element.properties.fillColor || 'rgba(74, 144, 226, 0.1)'
      ctx.beginPath()
      ctx.rect(
        element.position.x,
        element.position.y,
        element.properties.width,
        element.properties.height,
      )
      ctx.fill()
    }

    // Draw stroke
    ctx.strokeStyle = element.properties.color || '#4a90e2'
    ctx.lineWidth = element.properties.strokeWidth || 2
    ctx.beginPath()
    ctx.rect(
      element.position.x,
      element.position.y,
      element.properties.width,
      element.properties.height,
    )
    ctx.stroke()

    // Draw fixture label (prioritize custom label over fixtureType)
    const labelText =
      element.properties.label || element.properties.fixtureType || ''
    if (labelText) {
      ctx.save()
      ctx.fillStyle = element.properties.color || '#4a90e2'
      ctx.font = 'bold 16px Arial' // Larger and bold for better visibility
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'

      // Calculate label position with offset
      const defaultX = element.position.x + element.properties.width / 2
      const defaultY = element.position.y + element.properties.height / 2
      const labelOffsetX = element.labelPosition?.x || 0
      const labelOffsetY = element.labelPosition?.y || 0

      // Apply rotation if specified
      if (element.properties.labelRotation) {
        ctx.translate(defaultX + labelOffsetX, defaultY + labelOffsetY)
        ctx.rotate((element.properties.labelRotation * Math.PI) / 180)
        ctx.fillText(labelText, 0, 0)
      } else {
        ctx.fillText(
          labelText,
          defaultX + labelOffsetX,
          defaultY + labelOffsetY,
        )
      }
      ctx.restore()
    }
  }
}

const drawFixtureLineElement = (
  ctx: CanvasRenderingContext2D,
  element: MapElement,
) => {
  if (element.properties.endPosition) {
    // Draw fixture line with distinct styling
    ctx.strokeStyle = element.properties.color || '#4a90e2'
    ctx.lineWidth = element.properties.strokeWidth || 4
    ctx.lineCap = 'round'

    ctx.beginPath()
    ctx.moveTo(element.position.x, element.position.y)
    ctx.lineTo(
      element.properties.endPosition.x,
      element.properties.endPosition.y,
    )
    ctx.stroke()

    // Draw end caps to indicate fixture
    const angle = Math.atan2(
      element.properties.endPosition.y - element.position.y,
      element.properties.endPosition.x - element.position.x,
    )
    const capSize = 8

    // Start cap
    ctx.save()
    ctx.translate(element.position.x, element.position.y)
    ctx.rotate(angle + Math.PI / 2)
    ctx.beginPath()
    ctx.moveTo(-capSize / 2, 0)
    ctx.lineTo(capSize / 2, 0)
    ctx.stroke()
    ctx.restore()

    // End cap
    ctx.save()
    ctx.translate(
      element.properties.endPosition.x,
      element.properties.endPosition.y,
    )
    ctx.rotate(angle + Math.PI / 2)
    ctx.beginPath()
    ctx.moveTo(-capSize / 2, 0)
    ctx.lineTo(capSize / 2, 0)
    ctx.stroke()
    ctx.restore()
  }
}

const drawFixturePointElement = (
  ctx: CanvasRenderingContext2D,
  element: MapElement,
) => {
  // Draw fixture point (icon-like representation)
  const size = element.properties.size || 20
  ctx.fillStyle = element.properties.color || '#4a90e2'
  ctx.strokeStyle = element.properties.color || '#4a90e2'
  ctx.lineWidth = 2

  // Draw a circle with inner icon
  ctx.beginPath()
  ctx.arc(element.position.x, element.position.y, size / 2, 0, 2 * Math.PI)
  ctx.fill()

  // Draw inner icon based on fixture type
  ctx.strokeStyle = 'white'
  ctx.lineWidth = 2

  if (element.properties.fixtureType === 'sink') {
    // Draw sink icon (simplified basin shape)
    ctx.beginPath()
    ctx.arc(element.position.x, element.position.y, size / 3, 0, Math.PI)
    ctx.stroke()
  } else if (element.properties.fixtureType === 'toilet') {
    // Draw toilet icon (simplified oval)
    ctx.beginPath()
    ctx.ellipse(
      element.position.x,
      element.position.y,
      size / 3,
      size / 4,
      0,
      0,
      2 * Math.PI,
    )
    ctx.stroke()
  } else {
    // Default fixture icon (gear-like)
    ctx.beginPath()
    for (let i = 0; i < 6; i++) {
      const angle = (i * Math.PI * 2) / 6
      const x = element.position.x + Math.cos(angle) * (size / 3)
      const y = element.position.y + Math.sin(angle) * (size / 3)
      if (i === 0) {
        ctx.moveTo(x, y)
      } else {
        ctx.lineTo(x, y)
      }
    }
    ctx.closePath()
    ctx.stroke()
  }
}

const drawFixtureCircleElement = (
  ctx: CanvasRenderingContext2D,
  element: MapElement,
) => {
  if (element.properties.radius) {
    const centerX = element.position.x + element.properties.radius
    const centerY = element.position.y + element.properties.radius
    const radius = element.properties.radius

    // Handle background image or fill color
    if (element.properties.backgroundImage) {
      ctx.save()
      ctx.beginPath()
      ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI)
      ctx.clip() // Clip to circle shape

      // Get cached image
      const img = new Image()
      img.src = element.properties.backgroundImage

      // Apply background image opacity if set
      const oldAlpha = ctx.globalAlpha
      ctx.globalAlpha = element.properties.backgroundImageOpacity ?? 1

      // Draw based on fit mode
      const fitMode = element.properties.backgroundImageFit || 'cover'
      const diameter = radius * 2
      if (img.complete) {
        switch (fitMode) {
          case 'contain':
            const scale = Math.min(diameter / img.width, diameter / img.height)
            const scaledWidth = img.width * scale
            const scaledHeight = img.height * scale
            const x = centerX - scaledWidth / 2
            const y = centerY - scaledHeight / 2
            ctx.drawImage(img, x, y, scaledWidth, scaledHeight)
            break
          case 'stretch':
            ctx.drawImage(
              img,
              element.position.x,
              element.position.y,
              diameter,
              diameter
            )
            break
          case 'repeat':
            const pattern = ctx.createPattern(img, 'repeat')
            if (pattern) {
              ctx.fillStyle = pattern
              ctx.fill()
            }
            break
          case 'cover':
          default:
            const coverScale = Math.max(diameter / img.width, diameter / img.height)
            const coverWidth = img.width * coverScale
            const coverHeight = img.height * coverScale
            const coverX = centerX - coverWidth / 2
            const coverY = centerY - coverHeight / 2
            ctx.drawImage(img, coverX, coverY, coverWidth, coverHeight)
            break
        }
      }

      ctx.globalAlpha = oldAlpha
      ctx.restore()
    } else {
      // Draw fixture circle with distinct styling
      ctx.fillStyle = element.properties.fillColor || 'rgba(155, 89, 182, 0.2)'
      ctx.beginPath()
      ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI)
      ctx.fill()
    }

    // Draw stroke
    ctx.strokeStyle = element.properties.color || '#9b59b6'
    ctx.lineWidth = element.properties.strokeWidth || 2
    ctx.beginPath()
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI)
    ctx.stroke()

    // Draw fixture label
    const labelText =
      element.properties.label || element.properties.fixtureType || ''
    if (labelText) {
      ctx.save()
      ctx.fillStyle = element.properties.color || '#9b59b6'
      ctx.font = 'bold 16px Arial'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'

      const centerX = element.position.x + element.properties.radius
      const centerY = element.position.y + element.properties.radius
      const labelOffsetX = element.labelPosition?.x || 0
      const labelOffsetY = element.labelPosition?.y || 0

      ctx.fillText(labelText, centerX + labelOffsetX, centerY + labelOffsetY)
      ctx.restore()
    }
  }
}

const drawFixturePolygonElement = (
  ctx: CanvasRenderingContext2D,
  element: MapElement,
  mapSettings: MapSettings,
) => {
  if (element.properties.vertices) {
    const vertices = element.properties.vertices as Point[]

    ctx.beginPath()
    ctx.moveTo(vertices[0].x, vertices[0].y)
    for (let i = 1; i < vertices.length; i++) {
      ctx.lineTo(vertices[i].x, vertices[i].y)
    }
    ctx.closePath()

    // Handle background image or fill color
    if (element.properties.backgroundImage) {
      ctx.save()
      ctx.clip() // Clip to polygon shape

      // Get cached image
      const img = new Image()
      img.src = element.properties.backgroundImage

      // Find bounding box of polygon
      let minX = vertices[0].x, maxX = vertices[0].x
      let minY = vertices[0].y, maxY = vertices[0].y
      vertices.forEach(v => {
        minX = Math.min(minX, v.x)
        maxX = Math.max(maxX, v.x)
        minY = Math.min(minY, v.y)
        maxY = Math.max(maxY, v.y)
      })
      const width = maxX - minX
      const height = maxY - minY

      // Apply background image opacity if set
      const oldAlpha = ctx.globalAlpha
      ctx.globalAlpha = element.properties.backgroundImageOpacity ?? 1

      // Draw based on fit mode
      const fitMode = element.properties.backgroundImageFit || 'cover'
      if (img.complete) {
        switch (fitMode) {
          case 'contain':
            const scale = Math.min(width / img.width, height / img.height)
            const scaledWidth = img.width * scale
            const scaledHeight = img.height * scale
            const x = minX + (width - scaledWidth) / 2
            const y = minY + (height - scaledHeight) / 2
            ctx.drawImage(img, x, y, scaledWidth, scaledHeight)
            break
          case 'stretch':
            ctx.drawImage(img, minX, minY, width, height)
            break
          case 'repeat':
            const pattern = ctx.createPattern(img, 'repeat')
            if (pattern) {
              ctx.fillStyle = pattern
              ctx.fill()
            }
            break
          case 'cover':
          default:
            const coverScale = Math.max(width / img.width, height / img.height)
            const coverWidth = img.width * coverScale
            const coverHeight = img.height * coverScale
            const coverX = minX + (width - coverWidth) / 2
            const coverY = minY + (height - coverHeight) / 2
            ctx.drawImage(img, coverX, coverY, coverWidth, coverHeight)
            break
        }
      }

      ctx.globalAlpha = oldAlpha
      ctx.restore()
    } else {
      // Draw fill
      ctx.fillStyle = element.properties.fillColor || 'rgba(39, 174, 96, 0.2)'
      ctx.fill()
    }

    // Draw stroke
    ctx.strokeStyle = element.properties.color || '#27ae60'
    ctx.lineWidth = element.properties.strokeWidth || 2
    ctx.stroke()

    // Draw fixture label at centroid
    const labelText =
      element.properties.label || element.properties.fixtureType || ''
    if (labelText) {
      let centerX = 0,
        centerY = 0
      vertices.forEach(vertex => {
        centerX += vertex.x
        centerY += vertex.y
      })
      centerX /= vertices.length
      centerY /= vertices.length

      ctx.save()
      ctx.fillStyle = element.properties.color || '#27ae60'
      ctx.font = 'bold 16px Arial'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'

      const labelOffsetX = element.labelPosition?.x || 0
      const labelOffsetY = element.labelPosition?.y || 0

      ctx.fillText(labelText, centerX + labelOffsetX, centerY + labelOffsetY)
      ctx.restore()
    }
  }
}

const drawPreview = (
  ctx: CanvasRenderingContext2D,
  activeTool: Tool,
  drawStart: Point,
  mousePos: Point,
) => {
  ctx.save()
  ctx.strokeStyle = '#0969da'
  ctx.lineWidth = 2
  ctx.setLineDash([5, 5])

  switch (activeTool) {
    case 'wall':
    case 'door':
    case 'window':
    case 'line':
      ctx.beginPath()
      ctx.moveTo(drawStart.x, drawStart.y)
      ctx.lineTo(mousePos.x, mousePos.y)
      ctx.stroke()
      break

    case 'room':
    case 'stairs':
    case 'rectangle':
      const width = mousePos.x - drawStart.x
      const height = mousePos.y - drawStart.y
      ctx.beginPath()
      ctx.rect(drawStart.x, drawStart.y, width, height)
      ctx.stroke()
      break

    case 'circle':
      const radius = Math.sqrt(
        Math.pow(mousePos.x - drawStart.x, 2) +
          Math.pow(mousePos.y - drawStart.y, 2),
      )
      ctx.beginPath()
      ctx.arc(drawStart.x, drawStart.y, radius, 0, 2 * Math.PI)
      ctx.stroke()
      break

    case 'fixture-rect':
      const fixtureWidth = mousePos.x - drawStart.x
      const fixtureHeight = mousePos.y - drawStart.y
      ctx.strokeStyle = '#4a90e2'
      ctx.beginPath()
      ctx.rect(drawStart.x, drawStart.y, fixtureWidth, fixtureHeight)
      ctx.stroke()
      break

    case 'fixture-line':
      ctx.strokeStyle = '#4a90e2'
      ctx.lineWidth = 3
      ctx.beginPath()
      ctx.moveTo(drawStart.x, drawStart.y)
      ctx.lineTo(mousePos.x, mousePos.y)
      ctx.stroke()
      break

    case 'fixture-point':
      ctx.fillStyle = 'rgba(74, 144, 226, 0.5)'
      ctx.strokeStyle = '#4a90e2'
      ctx.beginPath()
      ctx.arc(mousePos.x, mousePos.y, 10, 0, 2 * Math.PI)
      ctx.fill()
      ctx.stroke()
      break

    case 'fixture-circle':
      const fixtureCircleRadius = Math.sqrt(
        Math.pow(mousePos.x - drawStart.x, 2) +
          Math.pow(mousePos.y - drawStart.y, 2),
      )
      ctx.strokeStyle = '#9b59b6'
      ctx.fillStyle = 'rgba(155, 89, 182, 0.2)'
      ctx.beginPath()
      ctx.arc(drawStart.x, drawStart.y, fixtureCircleRadius, 0, 2 * Math.PI)
      ctx.fill()
      ctx.stroke()
      break

    case 'fixture-polygon':
      // Handled by drawPolygonPreview
      break
  }

  ctx.restore()
}

const drawPolygonPreview = (
  ctx: CanvasRenderingContext2D,
  polygonVertices: Point[],
  mousePos: Point,
) => {
  ctx.save()
  ctx.strokeStyle = '#0969da'
  ctx.lineWidth = 2
  ctx.setLineDash([5, 5])

  // Draw the polygon path so far
  if (polygonVertices.length >= 2) {
    ctx.beginPath()
    ctx.moveTo(polygonVertices[0].x, polygonVertices[0].y)

    for (let i = 1; i < polygonVertices.length; i++) {
      ctx.lineTo(polygonVertices[i].x, polygonVertices[i].y)
    }

    ctx.stroke()
  }

  // Draw line from last vertex to current mouse position
  if (polygonVertices.length >= 1) {
    ctx.beginPath()
    const lastVertex = polygonVertices[polygonVertices.length - 1]
    ctx.moveTo(lastVertex.x, lastVertex.y)
    ctx.lineTo(mousePos.x, mousePos.y)
    ctx.stroke()
  }

  // Draw close line if we can close the polygon (3+ vertices)
  if (polygonVertices.length >= 3) {
    ctx.strokeStyle = '#ff6b6b'
    ctx.setLineDash([3, 3])
    ctx.beginPath()
    ctx.moveTo(mousePos.x, mousePos.y)
    ctx.lineTo(polygonVertices[0].x, polygonVertices[0].y)
    ctx.stroke()
  }

  // Draw vertex points
  ctx.fillStyle = '#0969da'
  ctx.setLineDash([])
  polygonVertices.forEach(vertex => {
    ctx.beginPath()
    ctx.arc(vertex.x, vertex.y, 4, 0, 2 * Math.PI)
    ctx.fill()
  })

  ctx.restore()
}

const drawMeasurement = (
  ctx: CanvasRenderingContext2D,
  startPoint: Point | null,
  endPoint: Point | null,
  mapSettings: MapSettings,
) => {
  if (!startPoint || !endPoint) return

  ctx.save()
  ctx.strokeStyle = '#e74c3c'
  ctx.lineWidth = 2
  ctx.setLineDash([5, 5])

  // Draw measurement line
  ctx.beginPath()
  ctx.moveTo(startPoint.x, startPoint.y)
  ctx.lineTo(endPoint.x, endPoint.y)
  ctx.stroke()

  // Draw end points
  ctx.setLineDash([])
  ctx.fillStyle = '#e74c3c'
  ctx.beginPath()
  ctx.arc(startPoint.x, startPoint.y, 4, 0, 2 * Math.PI)
  ctx.fill()
  ctx.beginPath()
  ctx.arc(endPoint.x, endPoint.y, 4, 0, 2 * Math.PI)
  ctx.fill()

  // Calculate and display distance
  const pixelDistance = Math.sqrt(
    Math.pow(endPoint.x - startPoint.x, 2) +
      Math.pow(endPoint.y - startPoint.y, 2),
  )
  const realDistance = pixelDistance / mapSettings.scale
  const units = mapSettings.units === 'feet' ? 'ft' : 'm'
  const distanceText = `${realDistance.toFixed(2)} ${units}`

  // Draw distance label
  const midX = (startPoint.x + endPoint.x) / 2
  const midY = (startPoint.y + endPoint.y) / 2

  ctx.font = 'bold 14px Arial'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'bottom'

  // Add background to text for better readability
  const textMetrics = ctx.measureText(distanceText)
  ctx.fillStyle = 'rgba(255, 255, 255, 0.9)'
  ctx.fillRect(
    midX - textMetrics.width / 2 - 4,
    midY - 20,
    textMetrics.width + 8,
    18,
  )

  ctx.fillStyle = '#e74c3c'
  ctx.fillText(distanceText, midX, midY - 4)

  ctx.restore()
}

export { drawElement, drawPreview, drawPolygonPreview, drawMeasurement }
